<template>
  <div v-if="!loading" class="whatsapp-page" :class="screenType">
    <div class="whatsapp-page__top-section" :style="topSectionStyle">
      <div
        v-if="topSectionImage"
        class="whatsapp-page__top-image"
        :class="{ 'whatsapp-page__top-image--smartphone': windowWidth < stackBreakpoint && !isUsingLogo, 'whatsapp-page__top-image--logo': isUsingLogo }"
      >
        <v-img v-if="!isUsingLogo" :src="topSectionImage" :alt="'WhatsApp'" cover />
        <img v-else :src="topSectionImage" :alt="'WhatsApp'" class="whatsapp-page__logo-image" />
      </div>
      <whatsapp-tutorial ref="tutorial" :steps="tutorialSteps" />
    </div>
    <div class="whatsapp-page__stores-cta">
      <v-btn
        class="whatsapp-page__stores-cta-btn"
        depressed
        x-large
        :color="getAssets.colors.whatsapp.contactButton"
        :style="{ color: getAssets.colors.whatsapp.contactButtonText }"
        @click="scrollToStoresSection"
      >
        Ver lojas
        <v-icon right class="whatsapp-page__stores-cta-icon" size="28">expand_more</v-icon>
      </v-btn>
    </div>
    <div ref="storesSection" class="whatsapp-page__stores-section" :style="topSectionStyle">
      <div class="whatsapp-page__stores-inner">
        <h2 class="whatsapp-page__stores-heading" :style="storesSectionHeadingStyle">Nossas lojas</h2>
        <p class="whatsapp-page__stores-description" :style="storesSectionHeadingStyle">Escolha sua loja favorita para receber nossas ofertas.</p>
        <v-text-field
          v-model="search"
          filled
          rounded
          hide-details
          placeholder="Buscar por nome ou endereço"
          prepend-inner-icon="search"
          clearable
          class="whatsapp-page__stores-search"
        />
      </div>
    </div>
    <div class="whatsapp-page__main-section" :style="mainSectionStyleWithPadding">
      <v-container>
        <v-row>
          <v-col v-for="(contact, index) in filteredWhatsappContacts" :key="index" cols="12" class="d-flex justify-center">
            <whatsapp-contact-card :contact="contact" />
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { screenTypeMixin } from '@/mixins/screen-type'
import { diacriticSpaceSensitiveRegex } from '@/helpers/search'
import WhatsappContactCard from '../components/WhatsappContactCard.vue'
import WhatsappTutorial from '../components/WhatsappTutorial.vue'

export default {
  name: 'WhatsApp',
  components: {
    WhatsappContactCard,
    WhatsappTutorial
  },
  mixins: [screenTypeMixin],
  data: () => ({
    whatsappContacts: [],
    tutorialHeight: 0,
    windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
    search: ''
  }),
  computed: {
    ...mapGetters(['GET_ASSETS', 'LOADING', 'GET_WHATSAPP_TUTORIAL_STEPS', 'GET_WHATSAPP_STACK_BREAKPOINT', 'GET_WHATSAPP_BOTS']),
    filteredWhatsappContacts() {
      if (!this.search) {
        return this.whatsappContacts
      }
      const searchRegex = diacriticSpaceSensitiveRegex(this.search)
      return this.whatsappContacts.filter(contact => searchRegex.test(contact.name) || searchRegex.test(contact.description))
    },
    getAssets() {
      return this.GET_ASSETS
    },
    loading() {
      return this.LOADING
    },
    tutorialSteps() {
      return this.GET_WHATSAPP_TUTORIAL_STEPS
    },
    stackBreakpoint() {
      return this.GET_WHATSAPP_STACK_BREAKPOINT
    },
    topSectionStyle() {
      return {
        'background-color': this.getAssets.colors.whatsapp.topSectionBackground
      }
    },
    topSectionImage() {
      // Get image based on screen width, fallback to desktop
      const images = this.getAssets.images?.whatsapp
      let imageUrl = null

      // Use smartphone image when screen width is less than stack breakpoint
      if (this.windowWidth < this.stackBreakpoint) {
        imageUrl = images?.smartphone
      } else {
        imageUrl = images?.desktop
      }

      // If image is empty, use finish logo as fallback
      if (!imageUrl || imageUrl.trim() === '') {
        return this.getAssets.images?.finish?.logo || null
      }
      return imageUrl
    },
    isUsingLogo() {
      // Check if we're using the logo instead of the WhatsApp image
      const images = this.getAssets.images?.whatsapp
      let imageUrl = null

      if (this.windowWidth < this.stackBreakpoint) {
        imageUrl = images?.smartphone
      } else {
        imageUrl = images?.desktop
      }

      return !imageUrl || imageUrl.trim() === ''
    },
    mainSectionStyle() {
      return {
        'background-color': this.getAssets.colors.whatsapp.mainSectionBackground
      }
    },
    mainSectionStyleWithPadding() {
      const baseStyle = this.mainSectionStyle

      return {
        ...baseStyle,
        'padding-top': '20px'
      }
    },
    /** Same as BaseLayout / toolbar: 'light' bg → dark text, dark bg → light text */
    storesSectionTitleContrast() {
      return this.$contrastHex(this.getAssets.colors.whatsapp.topSectionBackground)
    },
    storesSectionHeadingStyle() {
      return {
        color: this.storesSectionTitleContrast === 'light' ? '#ffffff' : '#000000'
      }
    }
  },
  watch: {
    tutorialSteps: {
      handler() {
        this.updateTutorialHeight()
      },
      deep: true
    }
  },
  async created() {
    await this.fetchBots()
  },
  mounted() {
    this.updateTutorialHeight()
    this.handleResize()
    window.addEventListener('resize', this.updateTutorialHeight)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTutorialHeight)
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async fetchBots() {
      try {
        const tag = this.$route.params.tag
        await this.$store.dispatch('fetchWhatsappBots', { tag })
        if (!this.GET_WHATSAPP_BOTS || this.GET_WHATSAPP_BOTS.length === 0) {
          this.$router.push({ path: '/home' }).catch(() => {})
          return
        }
        this.whatsappContacts = this.GET_WHATSAPP_BOTS.map(bot => ({
          name: bot.name,
          description: bot.description,
          number: bot.number,
          image: bot.image,
          message: bot.message,
          is_community_redirect: bot.is_community_redirect,
          community_link: bot.community_link
        }))
      } catch (error) {
        console.error('Error fetching WhatsApp bots:', error)
        this.$router.push({ path: '/home' }).catch(() => {})
      }
    },
    updateTutorialHeight() {
      this.$nextTick(() => {
        if (this.$refs.tutorial && this.$refs.tutorial.$el) {
          const tutorialCard = this.$refs.tutorial.$el.querySelector('.whatsapp-tutorial__card')
          if (tutorialCard) {
            this.tutorialHeight = tutorialCard.offsetHeight
          }
        }
      })
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    scrollToStoresSection() {
      const el = this.$refs.storesSection
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.whatsapp-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  &__top-section {
    min-height: 100px;
    padding: 40px 20px 0;
    position: relative;
    z-index: 2;
  }

  &__top-image {
    width: 100%;
    max-width: 1075px;
    aspect-ratio: 1075 / 400; // Desktop image aspect ratio (2.6875:1)
    margin: 0 auto;
    position: relative;
    z-index: 1;
    transform: translateY(100px);

    &--smartphone {
      max-width: 330px;
      aspect-ratio: 330 / 600; // Smartphone image aspect ratio (0.55:1)
    }

    &--logo {
      max-width: none;
      width: auto;
      height: auto;
      min-height: 0;
      aspect-ratio: unset;
      transform: translateY(100px);
      margin-bottom: 0;
    }
  }

  &__logo-image {
    width: auto;
    height: auto;
    max-width: 300px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }

  &__stores-cta {
    display: flex;
    justify-content: center;
    margin-top: 140px;
    margin-bottom: 20px;
    padding: 0 20px 16px;
    position: relative;
    z-index: 2;
  }

  &__stores-cta-btn {
    text-transform: none;
    font-weight: 600;
    min-height: 56px !important;
    padding: 0 32px !important;
    font-size: 1.125rem;
  }

  &__stores-cta-icon {
    opacity: 0.95;
  }

  &__stores-section {
    position: relative;
    z-index: 2;
    padding: 24px 100px 32px;
  }

  &__stores-inner {
    max-width: 900px;
    margin: 0 auto;
    width: 100%;
    text-align: center;

    .v-text-field {
      width: 100%;
    }
  }

  &__stores-heading {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 16px;
    line-height: 1.2;
    text-align: center;
  }

  &__stores-description {
    font-size: 1.125rem;
    font-weight: 400;
    margin: 0 0 16px;
    line-height: 1.2;
    text-align: center;
  }

  &__stores-search {
    ::v-deep .v-input__slot {
      background-color: #fff !important;
    }

    ::v-deep .v-text-field__slot input {
      color: #000 !important;
      caret-color: #000;
      text-align: left;
    }

    ::v-deep .v-text-field__slot input::placeholder {
      color: #000 !important;
      opacity: 0.55;
    }

    ::v-deep .v-input__prepend-inner .v-icon,
    ::v-deep .v-input__append-inner .v-icon {
      color: #000 !important;
      opacity: 0.55;
    }
  }

  &__main-section {
    flex: 1;
    padding: 0 0 40px;
    position: relative;
    z-index: 1;
  }
}

@media screen and (max-width: 960px) {
  .whatsapp-page {
    &__top-section {
      padding: 30px 20px;
      min-height: 150px;
    }

    &__stores-cta {
      margin-top: 100px;
    }

    &__stores-section {
      padding: 20px 100px 28px;
    }

    &__logo-image {
      max-width: 250px;
    }

    &__main-section {
      padding-bottom: 30px;
    }
  }
}

@media screen and (max-width: 600px) {
  .whatsapp-page {
    &__top-section {
      padding: 20px 16px;
      min-height: 120px;
    }

    &__stores-cta {
      margin-top: 120px;
    }

    &__stores-section {
      padding: 16px 40px 24px;
    }

    &__stores-heading {
      font-size: 1.25rem;
    }

    &__logo-image {
      max-width: 200px;
    }

    &__main-section {
      padding-bottom: 20px;
    }
  }
}
</style>
